/**
 * Assessment model constants for task rating options.
 *
 * ## Architecture Note
 *
 * The backend `AssessmentModel` enum (src/Portfolio/Enums/AssessmentModel.php)
 * only contains actual assessment models: `simple` and `grade`.
 *
 * The "none" option is **frontend-only** because:
 * - It represents the absence of assessment, not an assessment model
 * - The backend uses `allow_task_ratings = false` to indicate no assessment
 * - Adding NONE to the backend enum would require special handling in
 *   `getAssessmentModelClass()` and `getValueRules()` methods
 *
 * The frontend combines these into a single radio group for better UX:
 * - "Niet beoordelen" → `allow_task_ratings: false`, `assessment_models: null`
 * - "Voldaan / Niet voldaan" → `allow_task_ratings: true`, `assessment_models: 'simple'`
 * - "Cijfer" → `allow_task_ratings: true`, `assessment_models: 'grade'`
 *
 * @see src/Portfolio/Enums/AssessmentModel.php - Backend enum
 * @see TeacherTaskForm.vue - Form implementation
 * @see useTeacherTask.ts - Task info display
 */

/** Value for "no assessment" option (frontend-only, not from API) */
export const ASSESSMENT_MODEL_NONE = "none";

/**
 * Labels for assessment models.
 * Used as fallback when API data isn't available (e.g., in task info display).
 * These should match the labels from the backend AssessmentModel::label() method.
 */
export const ASSESSMENT_MODEL_LABELS: Record<string, string> = {
  [ASSESSMENT_MODEL_NONE]: "Niet beoordelen",
  simple: "Voldaan / Niet voldaan",
  grade: "Cijfer",
};

/**
 * Action button labels for grading per assessment model.
 */
export const ASSESSMENT_MODEL_ACTION_LABELS: Record<string, string> = {
  simple: "Beoordelen",
  grade: "Cijfer geven",
};

/**
 * Value labels for simple assessments.
 * Maps the boolean value to a human-readable label.
 */
export const SIMPLE_ASSESSMENT_VALUE_LABELS: Record<string, string> = {
  true: "Voldaan",
  false: "Niet voldaan",
};

/**
 * Button colors for simple assessment grading buttons.
 * Maps the boolean value to a c-button color.
 */
export const SIMPLE_ASSESSMENT_BUTTON_COLORS: Record<string, string> = {
  true: "primary",
  false: "navy",
};

/**
 * Formats an assessment value to a human-readable label.
 * Works for both simple (boolean) and grade (numeric) assessment types.
 */
export const formatAssessmentValue = (
  type: string,
  value: unknown,
): string | null => {
  if (type === "simple") {
    return SIMPLE_ASSESSMENT_VALUE_LABELS[String(value)] ?? null;
  }

  if (type === "grade") {
    return value != null ? String(value) : null;
  }

  return null;
};

/**
 * Assessment model type definitions used for type validation and lookup.
 * Previously in models/portfolio/assessments/AssessmentModels.js.
 */
export const ASSESSMENT_MODEL_TYPES: Record<
  string,
  { name: string; label: string; type: string }
> = {
  simple: {
    name: "Simpel",
    label: "Voldaan/niet voldaan",
    type: "simple",
  },
  grade: {
    name: "Cijfer",
    label: "Cijfer beoordeling",
    type: "grade",
  },
};

export const DEFAULT_RATING_MODEL = {
  id: null,
  type: "simple",
  value: null,
  explanation: "",
  allow_retry: null,
  assessed_at: null,
  assessed_by_shortage: null,
  assessed_by: null,
};
